import React, { useState, useEffect } from 'react';
import { Col, Row } from 'antd';
import styled from 'styled-components';
import GlobalButton from 'Components/common/components/GlobalButton';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCompanyProfile, selectProfileLoading,
  selectCompanyNews, selectNewsFilterApplied,
} from 'Selector/CompaniesSelector';
import { Section } from 'Types/CompaniesTypes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isAPrivateCompany, smoothBehavior } from 'Utils/UtilityFunctions';
import { actions as companiesActions } from 'Slice/CompaniesSlice';
import SkeletonCustom from 'Components/common/skeleton/SkeletonCustom';
import { Article } from 'Types/ArticleTypes';
import { maxMobile, tabHeading } from 'Utils/utilsStyle';
import { COMPANY_NEWS_ROUTE } from 'Utils/Constants';
import FinancialStatsWidget from '../../common/components/FinancialStatsWidget';
import FolderIcon from '../svgIcons/FolderIcon';
import PasteIcon from '../svgIcons/PasteIcon';
import DollarIcon from '../svgIcons/DollarIcon1';
import SecFilling from './SecFilling';
import FinancialStatsSkeleton from '../../common/skeleton/FinancialStatsSkeleton';
import GlobalButtonSkeleton from '../../common/skeleton/GlobalButtonSkeletony';
import LazyCharts from './FinancialsStockCharts';

const Wrapper = styled.div`
  margin-bottom: 27px;
  .company-financial-heading {
    ${tabHeading};
    @media all and (max-width: ${maxMobile}) {
      padding: 8px 2px 0 2px;
      line-height: 22px;
    }
  }
  .charts-container{
    width: 100%;
    justify-content: space-between;
    padding-left: 10px;
    padding-right: 10px;
  }
  .financial-stats-wrapper {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -10px;
  }
`;

type Props = {
  redirect: string,
};

const Financials = function Financials(props: Props) {
  const { redirect } = props;
  const dispatch = useDispatch();
  const companyProfile = useSelector(selectCompanyProfile);
  const isLoading = useSelector(selectProfileLoading);
  // const flurryObject = useSelector(selectFinanialFlurry);
  const [showSecFilling, setShowSecFilling] = useState(false);
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId');
  const companyIdParsed = companyId ? parseInt(companyId, 10) : 0;
  // const isCalled = useSelector(selectFinanialCall);
  const companyNewsArticles: Article[] | null = useSelector(selectCompanyNews);
  const newsFilterApplied: boolean = useSelector(selectNewsFilterApplied);

  const navigate = useNavigate();
  

  const getFinancialStatsSkeleton = () => {
    const skeletons = [];
    for (let i = 0; i < 10; i += 1) {
      skeletons.push(
        <Col span={4}>
          <FinancialStatsSkeleton />
        </Col>,
      );
    }
    return skeletons;
  };

  const getGlobalButtonSkeleton = () => {
    const skeletons = [];
    for (let i = 0; i < 3; i += 1) {
      skeletons.push(
        <Col span={8}>
          <GlobalButtonSkeleton />
        </Col>,
      );
    }
    return skeletons;
  };

  const getGlobalButton = (title: string, func: () => void, icon = <DollarIcon />) => (
    <Col span={8}>
      <GlobalButton
        title={title}
        IconLeft={icon}
        textColor="#424B50"
        fontSize="24px"
        fontSizeSm="20px"
        bgColor="#fff"
        innerSpace="20px 38px"
        buttonClassName="w-100 res-sm-md"
        onButtonClick={func}
        hoverFin
      />
    </Col>
  );

  const getEarningAndReports = () => {
    if (companyProfile) {
      const { earningAndReports } = companyProfile.companyFinancialInfo;
      const buttonsInfo = [
        {
          name: 'latestEarnings',
          icon: <DollarIcon />,
        },
        {
          name: 'annualReport',
          icon: <PasteIcon />,
        },
        {
          name: 'secFiling',
          icon: <FolderIcon />,
        },
      ];

      const buttons = [];

      buttons.push(
        (() => {
          const index: number = earningAndReports.findIndex(
            (item) => item.name === buttonsInfo[0].name,
          );

          if (index === -1 || !earningAndReports[index]?.alerts?.alertId
            || (!companyNewsArticles?.length && !newsFilterApplied)) return null;
          return getGlobalButton(
            earningAndReports[index].title,
            () => {
              dispatch(companiesActions.setNewsFilterApplied());
              dispatch(
                companiesActions.setCompanyNewsFiltersApplied(
                  `${earningAndReports[index]?.alerts?.alertId}`,
                ),
              );
              dispatch(companiesActions.setLatestEarningScroll(true));
              navigate(`${COMPANY_NEWS_ROUTE}${companyIdParsed}`);
            },
            buttonsInfo[0].icon,
          );
        })(),
      );

      buttons.push(
        (() => {
          const index: number = earningAndReports.findIndex(
            (item) => item.name === buttonsInfo[1].name,
          );

          if (index === -1 || !earningAndReports[index].value.length || earningAndReports[index].value === 'N/A') return null;
          return getGlobalButton(
            earningAndReports[index].title,
            () => window.open(earningAndReports[index].value),
            buttonsInfo[1].icon,
          );
        })(),
      );

      buttons.push(
        (() => {
          const index: number = earningAndReports.findIndex(
            (item) => item.name === buttonsInfo[2].name,
          );

          if (index === -1) return null;
          return getGlobalButton(
            earningAndReports[index].title,
            () => setShowSecFilling(true),
            buttonsInfo[2].icon,
          );
        })(),
      );

      return buttons;
    }
    return null;
  };

  useEffect(() => {
    if (companyProfile && isAPrivateCompany(companyProfile)) {
      navigate(`/company/news/?companyId=${companyIdParsed}`);
    }
  }, [companyProfile]);

  useEffect(() => {
    if (redirect === 'financials') {
      setShowSecFilling(true);
      if (!isLoading && !searchParams.get('redirectTo')) {
        smoothBehavior();
      }
    }
  }, [redirect, isLoading, searchParams]);

  return (
    companyProfile && !isAPrivateCompany(companyProfile) ? (
      <Wrapper id="tabs-details">
        <span className="company-financial-heading">
          Financials
        </span>
        {
          !showSecFilling ? (
            <>
              <Row gutter={[20, 10]} className="states-pills-button">
                {
                  companyProfile && !isAPrivateCompany(companyProfile) ? (
                    
                      <LazyCharts />
                    
                  ) : null
                }
              </Row>
              {
                companyProfile && !isAPrivateCompany(companyProfile) ? (
                  <Row gutter={[20, 10]} className="mt-c-15 fin-inner-details">
                    {
                      isLoading ? getGlobalButtonSkeleton() : getEarningAndReports()
                    }
                  </Row>
                ) : null
              }
              <Row className="Grid-width-row col-six-fix col-six-md-sm" gutter={[24, 24]}>
                {isLoading ? getFinancialStatsSkeleton()
                  : companyProfile?.companyFinancialInfo?.sections.map((section: Section) => (
                    <Col span={4} className="gutter-row">
                      <FinancialStatsWidget companyName={section.title} title={`${section.currencySymbol || ''}${section.value}`} />
                    </Col>
                  ))}
              </Row>
            </>
          ) : <SecFilling setShowSecFilling={setShowSecFilling} />
        }
      </Wrapper>
    ) : null
  );
};

export default React.memo(Financials);
